// modules
import { useCallback, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";

// constants
import { ReimbursementVerificationReview, Routes } from "constants/global";

// components
import CountDownModal from "components/molecules/ModalContainer/CountDownModalContainer";

// redux
import { useAppDispatch, useAppSelector } from "app/hooks/hooks";
import {
	rejectOrApproveVerificationClaim,
	verificationClaimDetailsSelector,
	verificationClaimLoadingSelector,
	approveReimbursementClaim,
	approvalClaimDetailsSelector,
	approvalClaimLoadingSelector
} from "redux/reimbursement/reimbursementSlice";
import { getConfigSelector } from "redux/userList/userSlice";

// utils
import { allowApproveOrReject, getAttachmentsList } from "utils/reimbursementVerification";
import { validateAmount, validateRejectRetroClaimReason } from "utils/Validations/fieldValidations";

type ReimVerRejectClaimFields = {
	reim_amount: string;
	rejection_reason: string;
};

type ReimVerRejectClaimFieldErrs = {
	reim_amount: boolean;
	rejection_reason: boolean;
};

const getClaimDetailsSelector = (pathname: string) => {
	return pathname.includes(Routes.DECLARATION_VERIFICATION) ? verificationClaimDetailsSelector : approvalClaimDetailsSelector;
};

const getClaimDetailsLoadingSelector = (pathname: string) => {
	return pathname.includes(Routes.DECLARATION_VERIFICATION) ? verificationClaimLoadingSelector : approvalClaimLoadingSelector;
};

export function useReimbursementVerificationRejectClaim() {
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const { pathname } = useLocation();

	const claimDetails = useAppSelector(getClaimDetailsSelector(pathname));
	const claimsLoading = useAppSelector(getClaimDetailsLoadingSelector(pathname)) === 1;
	const configDetails = useAppSelector(getConfigSelector);
	const { data } = claimDetails || {};
	const { data: configDetailsData } = configDetails || {};

	const [fieldDetails, setFieldDetails] = useState<ReimVerRejectClaimFields>({
		reim_amount: "",
		rejection_reason: ""
	});
	const [fieldErrs, setFieldErrs] = useState<ReimVerRejectClaimFieldErrs>({
		reim_amount: false,
		rejection_reason: false
	});

	const initialValues = useMemo(() => {
		if (data && data?.reimb_summ && data?.reimb_summ?.length > 0) {
			return data?.reimb_summ[0];
		}
		return {};
	}, [data, claimsLoading, configDetailsData]);

	const goBack = useCallback(() => {
		const backPage = pathname.includes(Routes.DECLARATION_VERIFICATION) ? Routes.DECLARATION_VERIFICATION : Routes.DECLARATION_APPROVAL;
		navigate(`${backPage}`);
	}, [navigate]);

	const navigateToVerificationPage = useCallback(() => {
		const navigateTo = pathname.includes(Routes.DECLARATION_VERIFICATION) ? Routes.DECLARATION_VERIFICATION : Routes.DECLARATION_APPROVAL;
		navigate(`${navigateTo}`);
	}, [navigate]);

	const rejectClaimSuccess = useCallback(() => {
		CountDownModal({
			timer: 3,
			title: ReimbursementVerificationReview?.REJECT_CLAIM_MESSAGE,
			onTimeComplete: navigateToVerificationPage
		});
	}, [CountDownModal, navigateToVerificationPage]);

	const validateFields = useCallback(
		(key?: string) => {
			const errs: ReimVerRejectClaimFieldErrs = {
				...fieldErrs
			};
			if (!key) {
				if (!fieldDetails?.reim_amount) {
					errs.reim_amount = true;
				} else {
					errs.reim_amount = !validateAmount(fieldDetails?.reim_amount);
				}

				if (!fieldDetails?.rejection_reason) {
					errs.rejection_reason = true;
				} else {
					errs.rejection_reason = !validateRejectRetroClaimReason(fieldDetails?.rejection_reason);
				}
			}

			if (key && !fieldDetails[key as keyof ReimVerRejectClaimFields]) {
				errs[key as keyof ReimVerRejectClaimFieldErrs] = true;
			} else if (key && fieldDetails[key as keyof ReimVerRejectClaimFields]) {
				if (key === "reim_amount") {
					errs[key as keyof ReimVerRejectClaimFieldErrs] = !validateAmount(fieldDetails?.reim_amount);
				}

				if (key === "rejection_reason") {
					errs[key as keyof ReimVerRejectClaimFieldErrs] = !validateRejectRetroClaimReason(fieldDetails?.rejection_reason);
				}
			}

			setFieldErrs({ ...errs });

			return errs;
		},
		[fieldErrs, fieldDetails, validateRejectRetroClaimReason]
	);

	const handleRejectClaim = useCallback(async () => {
		const errs = validateFields();

		if (!errs?.reim_amount && !errs?.rejection_reason && enableApproveRejectButtons) {
			const payload = {
				reimb_summ_id: Number(initialValues?.reimb_summ_id),
				reject_claim: "X",
				remarks: fieldDetails?.rejection_reason,
				amount: Number(fieldDetails?.reim_amount),
				voucher_group_code: initialValues?.voucher_group_code
			};

			try {
				const dispatchMethod = pathname.includes(Routes.DECLARATION_VERIFICATION) ? rejectOrApproveVerificationClaim : approveReimbursementClaim;
				const { message } = await dispatch(dispatchMethod(payload)).unwrap();
				if (message === ReimbursementVerificationReview.REJECT_CLAIM_MESSAGE) {
					rejectClaimSuccess();
				}
			} catch (error) {
				// axios error
				const err = error as any;
				if (err?.response?.status === 401) {
					navigate(Routes.LOGIN);
				} else {
					CountDownModal({
						timer: 3,
						title: err?.response?.data?.errors?.[0]?.message,
						errorIcon: true
					});
				}
			}
		}
	}, [fieldErrs, initialValues, fieldDetails, CountDownModal, rejectClaimSuccess]);

	const enableApproveRejectButtons = useMemo(() => {
		return allowApproveOrReject(configDetailsData);
	}, [configDetailsData, claimsLoading, data]);

	const handleChangeDetails = useCallback(
		(key: string, value: string) => {
			setFieldDetails({
				...fieldDetails,
				[key as keyof ReimVerRejectClaimFields]: value
			});

			setFieldErrs({
				...fieldErrs,
				[key]: false
			});
		},
		[fieldDetails]
	);

	return {
		claimDetails: initialValues,
		documents: getAttachmentsList(initialValues?.tenant_documents || ""),
		fieldDetails,
		fieldErrs,
		enableApproveRejectButtons,
		claimsLoading,
		validateFields,
		handleChangeDetails,
		handleRejectClaim,
		goBack
	};
}
